import { Instagram, Mail, Phone, ShieldCheck } from "lucide-react";
import LogoLight from "@/assets/logo/icone/png/system.png";
import LogoDark from "@/assets/logo/icone/png/white.png";
import { useTheme } from "@/lib/theme-provider";
import { Link, useLocation } from "react-router-dom";
import { useDataFastGoals } from "@/hooks/useDataFastGoals";
import { buttonClass, CONTAINER } from "./ui-tokens";

const socialLinks = [
  { href: "https://instagram.com/ecclesiato", label: "Instagram", icon: Instagram },
  { href: "mailto:suporte@ecclesiato.com.br", label: "Email", icon: Mail },
  { href: "https://wa.me/5519993376029", label: "WhatsApp", icon: Phone },
];

// Âncoras da home. Ao remover seção da home, passe por aqui e pelo cabeçalho.
const productLinks = [
  { label: "Solução", href: "#solucao" },
  { label: "Recursos", href: "#recursos" },
  { label: "Depoimentos", href: "#depoimentos" },
  { label: "Preços", href: "#precos" },
  { label: "FAQ", href: "#faq" },
];

/**
 * Páginas próprias, não âncoras da landing. Lista separada porque uma usa
 * `Link` (rota) e a outra `<a href="#">` (âncora).
 */
const productPages = [
  { label: "Todos os módulos", to: "/features" },
  { label: "Comparativos", to: "/comparar" },
  { label: "Ferramentas grátis", to: "/ferramentas" },
  { label: "Diagnóstico da gestão", to: "/diagnostico" },
  { label: "Blog", to: "/blog" },
  { label: "Comunidade", to: "/comunidade" },
];

const legalLinks = [
  { label: "Política de Privacidade", to: "/politica-privacidade" },
  { label: "Política de Reembolsos", to: "/politica-reembolsos" },
  { label: "Termos de Uso", to: "/termos-de-uso" },
];

const linkClass =
  "text-sm text-muted-foreground transition-colors duration-150 hover:text-foreground";

export default function Footer() {
  const { resolvedTheme } = useTheme();
  const { trackFooterLinkClicked } = useDataFastGoals();
  // O rodapé aparece em toda página, e `productLinks` são âncoras da landing.
  // Sem o prefixo, em /pricing ou /blog os links apontavam para seções que
  // não existem ali e o clique não fazia nada.
  const { pathname } = useLocation();

  return (
    <footer className="border-t border-border bg-background">
      {/* O `pb` extra no mobile é o espaço da barra fixa de CTA
          (sticky-cta.tsx, ~70px): sem essa folga ela cobria o copyright. */}
      <div className={`${CONTAINER} pt-16 pb-10 max-md:pb-28`}>
        <div className="grid gap-12 md:grid-cols-12">
          {/* ── Marca ─────────────────────────────────────────────────── */}
          <div className="md:col-span-5">
            <div className="flex items-center gap-2.5">
              <img
                src={resolvedTheme === "dark" ? LogoDark : LogoLight}
                alt=""
                className="size-8 object-contain"
                width={32}
                height={32}
                loading="lazy"
              />
              <span className="text-base font-semibold tracking-tight text-foreground">
                Ecclesiato
              </span>
            </div>

            <p className="mt-5 max-w-sm text-sm leading-relaxed text-muted-foreground">
              O sistema de gestão que devolve tempo ao pastoreio: membros,
              visitantes, escalas, células, kids e financeiro no mesmo lugar.
            </p>

            <Link
              to="/register?plan=starter&period=monthly"
              onClick={() => trackFooterLinkClicked("footer_cta")}
              className={buttonClass("primary", "md", "mt-6")}
            >
              Criar conta em 2 minutos
            </Link>

            <ul className="mt-6 -ml-2.5 flex gap-1">
              {socialLinks.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    target={link.href.startsWith("http") ? "_blank" : undefined}
                    rel="noreferrer"
                    onClick={() => trackFooterLinkClicked(link.label)}
                    className="grid size-11 place-items-center rounded-full text-muted-foreground transition-colors duration-150 hover:bg-accent hover:text-foreground"
                    aria-label={link.label}
                  >
                    <link.icon className="size-4.5" strokeWidth={1.5} aria-hidden="true" />
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* ── Produto ───────────────────────────────────────────────── */}
          <nav aria-labelledby="rodape-produto" className="md:col-span-3">
            <p id="rodape-produto" className="text-sm font-semibold text-foreground">
              Produto
            </p>
            <ul className="mt-4 space-y-3">
              {productLinks.map((link) => (
                <li key={link.label}>
                  <a
                    href={pathname === "/" ? link.href : `/${link.href}`}
                    onClick={() => trackFooterLinkClicked(link.label)}
                    className={linkClass}
                  >
                    {link.label}
                  </a>
                </li>
              ))}
              {productPages.map((link) => (
                <li key={link.label}>
                  <Link
                    to={link.to}
                    onClick={() => trackFooterLinkClicked(link.label)}
                    className={linkClass}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* ── Legal ─────────────────────────────────────────────────── */}
          <div className="md:col-span-4">
            <nav aria-labelledby="rodape-legal">
              <p id="rodape-legal" className="text-sm font-semibold text-foreground">
                Legal
              </p>
              <ul className="mt-4 space-y-3">
                {legalLinks.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.to}
                      onClick={() => trackFooterLinkClicked(link.label)}
                      className={linkClass}
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </nav>

            <p className="mt-8 flex max-w-xs gap-3 text-sm leading-relaxed text-muted-foreground">
              <ShieldCheck
                className="mt-0.5 size-4.5 shrink-0 text-primary"
                strokeWidth={1.75}
                aria-hidden="true"
              />
              <span>
                <span className="font-medium text-foreground">
                  7 dias grátis para testar.
                </span>{" "}
                Dados protegidos conforme a LGPD, em servidores Google Cloud.
              </span>
            </p>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-start justify-between gap-2 border-t border-border pt-6 text-sm text-muted-foreground md:flex-row md:items-center">
          <p>
            &copy; {new Date().getFullYear()} Ecclesiato. Todos os direitos
            reservados.
          </p>
          <p>Feito com cuidado para igrejas brasileiras.</p>
        </div>
      </div>
    </footer>
  );
}
